const numericThickness = (value: unknown): number => {
	const raw = value && typeof value === 'object' ? (value as Record<string, any>).thickness : value;
	if (typeof raw === 'number') return Number.isFinite(raw) && raw > 0 ? raw : 0;
	const match = String(raw ?? '').replace(',', '.').match(/(?:\d+(?:\.\d*)?|\.\d+)/);
	if (!match) return 0;
	const parsed = Number(match[0]);
	return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

/** Catalog thickness is string-backed (for example `06mm` or `0.72mm`). */
export const parseMaterialThickness = (material: unknown): number => {
	if (!material || typeof material !== 'object') return 0;
	return Math.ceil(numericThickness((material as Record<string, any>).thickness));
};

export const deriveMaterialStackThickness = (
	core: unknown,
	innerFinish: unknown,
	outerFinish: unknown,
	fallback = 18
): number => {
	const parts = [core, innerFinish, outerFinish].map(parseMaterialThickness);
	const total = parts.reduce((sum, thickness) => sum + thickness, 0);
	return total > 0 ? total : Math.max(0, Math.ceil(numericThickness(fallback))) || 18;
};

/** Ensures every furniture-builder request uses its selected material stack. */
export const withDerivedBuilderThickness = <T extends Record<string, any>>(payload: T): T => {
	if (!payload || typeof payload !== 'object') return payload;
	const next: Record<string, any> = { ...payload };

	if ('carcassThickness' in next || 'carcassCore' in next) {
		next.carcassThickness = deriveMaterialStackThickness(
			next.carcassCore,
			next.carcassInnerFinish,
			next.carcassOuterFinish,
			next.carcassThickness
		);
	}

	if ('shutterThickness' in next || 'shutterCore' in next) {
		next.shutterThickness = deriveMaterialStackThickness(
			next.shutterCore,
			next.shutterInnerFinish,
			next.shutterOuterFinish,
			next.shutterThickness
		);
	}

	if ('panelThickness' in next || 'wardrobeType' in next || 'internalType' in next) {
		next.panelThickness = deriveMaterialStackThickness(
			next.carcassCore,
			next.carcassInnerFinish,
			next.carcassOuterFinish,
			next.panelThickness
		);
	}

	if ('thickness' in next && ('core' in next || 'innerFinish' in next || 'outerFinish' in next)) {
		next.thickness = deriveMaterialStackThickness(next.core, next.innerFinish, next.outerFinish, next.thickness);
	}

	return next as T;
};
